import { debounce } from "lodash-es";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RECORDING_STARTED_EVENT_NAME,
  TEST_TYPES,
} from "../../../../constants";
import { TextField } from "../../../components";
import { WordCloudWrapper, WordCloudWrapperProps } from "./WordCloudWrapper";
import { SliderWrapper, SliderWrapperProps } from "./SliderWrapper";
import { AreaSliderWrapper, AreaWrapperProps } from "./AreaSliderWrapper";
import { DtoTestSetItem } from "@/types/dto-types";
import { GenericObject } from "@/types";

import "./styles.scss";

const VIDEO_END_DELAY = 500;

export interface TestSetItemProps extends React.HTMLAttributes<HTMLDivElement> {
  item: DtoTestSetItem;
  onChange: (data: GenericObject, isTextInput?: boolean) => void;
  onBlur: (e: any) => void;
  onNextClick: () => void;
  initialValue: unknown | undefined;
  initialRawValue: unknown | undefined;
  isVideoStep: boolean;
}

function TestItem(props: TestSetItemProps) {
  const {
    item,
    onChange,
    onNextClick,
    initialValue,
    initialRawValue,
    isVideoStep,
    onBlur,
    ...rest
  } = props;
  const [value, setValue] = useState(initialValue);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    window.addEventListener(RECORDING_STARTED_EVENT_NAME, () => {
      videoRef.current?.pause();
    });
  }, [videoRef]);

  const debounceCallback = useMemo(
    () => debounce((data: any) => onChange(data, false), 100),
    [onChange]
  );

  function updateValueState(e: any) {
    setValue(e.target.value);
    return {
      [`${item.dbColumn}`]: e.target.value,
      [`${item.dbColumn}Raw`]: e.target.rawValue,
    };
  }

  function onValueChange(e: any) {
    const data = updateValueState(e);
    debounceCallback(data);
  }

  function onTextInputValueChange(e: any) {
    const data = updateValueState(e);
    onChange(data, true);
  }

  function onTextInputBlur(e: any) {
    onBlur(e);
  }

  return (
    <div {...rest}>
      <div
        className="tg_test-sub-heading-html"
        dangerouslySetInnerHTML={{ __html: item.text }}
      ></div>
      {item.type !== TEST_TYPES.SUB_HEADING && (
        <>
          {isVideoStep ? (
            <>
              <video
                ref={videoRef}
                className="tg_video"
                width="100%"
                src={item.video}
                controls
                autoPlay
                playsInline
                webkit-playsinline="true"
                onEnded={() => setTimeout(onNextClick, VIDEO_END_DELAY)}
              ></video>
            </>
          ) : (
            <>
              {item.type === TEST_TYPES.TEXT && (
                <TextField
                  name={`textarea-${item._id}`}
                  textarea
                  rows="6"
                  value={value}
                  onChange={onTextInputValueChange}
                  onBlur={onTextInputBlur}
                />
              )}
              {item.type === TEST_TYPES.AreaSlider && (
                <AreaSliderWrapper
                  item={item as unknown as AreaWrapperProps["item"]}
                  onChange={onValueChange}
                  value={initialValue as AreaWrapperProps["value"]}
                />
              )}
              {(item.type === TEST_TYPES.SingleSlider ||
                item.type === TEST_TYPES.DoubleSlider) && (
                <SliderWrapper
                  item={item as unknown as SliderWrapperProps["item"]}
                  onChange={onValueChange}
                  value={initialValue as SliderWrapperProps["value"]}
                />
              )}
              {item.type === TEST_TYPES.WordCloud && (
                <WordCloudWrapper
                  item={item as unknown as WordCloudWrapperProps["item"]}
                  onChange={onValueChange}
                  rawValue={
                    initialRawValue as WordCloudWrapperProps["rawValue"]
                  }
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TestItem;
